/**
 * 👍 / 👎 crowdsourced quality votes on explanation cards.
 */

import { useState } from 'react';
import { api } from '../lib/api-client';
import { resolveTelemetryUserId } from '../lib/auth-session';

interface QuestionVoteBarProps {
    questionId: string;
}

export function QuestionVoteBar({ questionId }: QuestionVoteBarProps) {
    const [vote, setVote] = useState<-1 | 1 | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const send = async (next: -1 | 1) => {
        if (busy) return;
        const previous = vote;
        setVote(next);
        setBusy(true);
        setError('');
        try {
            await api.voteQuestionFeedback({
                userId: resolveTelemetryUserId(),
                questionId,
                vote: next,
            });
        } catch {
            setVote(previous);
            setError('Could not save vote');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="question-vote-bar" role="group" aria-label="Question quality vote">
            <span className="question-vote-bar__label">Was this explanation helpful?</span>
            <button
                type="button"
                className={`question-vote-btn${vote === 1 ? ' question-vote-btn--active' : ''}`}
                aria-pressed={vote === 1}
                aria-label="Thumbs up"
                disabled={busy}
                onClick={() => void send(1)}
            >
                👍
            </button>
            <button
                type="button"
                className={`question-vote-btn${vote === -1 ? ' question-vote-btn--active' : ''}`}
                aria-pressed={vote === -1}
                aria-label="Thumbs down"
                disabled={busy}
                onClick={() => void send(-1)}
            >
                👎
            </button>
            {error ? <span className="question-vote-bar__error">{error}</span> : null}
        </div>
    );
}
